import { useEffect, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";

import { useWizardContext } from "../contexts";
import { useReserve, useTrackCompra } from ".";
import { useEvento } from "@/store/evento/evento";
import { useInvalidateOccupiedSeats } from "./useOccupiedSeats";
import { useInvalidateReservedStock } from "./useReservedStock";

import { CheckoutPayload, DatosComprador } from "@/schemas/checkout.schema";
import { WIZARD_STEPS, COMPRA_STATUS } from "../constants";

/**
 * Hook que orquesta toda la lógica del wizard de pago.
 * 
 * Responsabilidades:
 * - Sincronización form ↔ store (comprador)
 * - Manejo de reservas según el step
 * - Handlers de navegación (back, close)
 * - Estados de UI (loading, error, hideBack)
 */
export const useWizardOrchestration = (
    methods: UseFormReturn<CheckoutPayload>,
    onClose: () => void
) => {
    const { step, back } = useWizardContext();
    const { release, reservaId, reserve } = useReserve();
    const { closeSSE } = useTrackCompra();

    const { set: setCompraStatus } = useEvento.use.compra();
    const { set: setComprador } = useEvento.use.comprador();
    const { state: funcion } = useEvento.use.funcion();
    const { state: entradas } = useEvento.use.entradas();

    const clearAll = useEvento.use.clearAll();
    const invalidateOccupiedSeats = useInvalidateOccupiedSeats();
    const invalidateReservedStock = useInvalidateReservedStock();

    const { state: isLoading, set: setIsLoading } = useEvento.use.isLoading();
    const { state: error, set: setError } = useEvento.use.error();

    const hideBack = step === WIZARD_STEPS.RESUMEN || step === WIZARD_STEPS.COMPLETADO;

    // ============================================
    // Handlers
    // ============================================
    const handleBack = useCallback(async () => {
        if (step === WIZARD_STEPS.DATOS_COMPRADOR && reservaId) {
            await release();
        }
        back();
    }, [step, reservaId, release, back]);

    const handlePanelClose = useCallback(async () => {
        if (reservaId) {
            await release();
        }
        // Invalidar cache para forzar refetch y mostrar stock actualizado
        if (funcion?.id) {
            invalidateOccupiedSeats(funcion.id);  // Para eventos con mapa
            invalidateReservedStock(funcion.id);  // Para eventos sin mapa
        }
        // Mantener selección de función y entradas para verificar conflictos
        clearAll({ tabs: 'funciones', funcion, entradas });
        closeSSE();
        methods.reset();
        onClose();
    }, [reservaId, release, funcion, entradas, clearAll, invalidateOccupiedSeats, invalidateReservedStock, closeSSE, methods, onClose]);

    // ============================================
    // Effects
    // ============================================

    // Sincronizar form con store (comprador)
    const syncCompradorStore = () => {
        const subscription = methods.watch((value, { name }) => {
            if (name?.startsWith('comprador') && value.comprador) {
                setComprador({ ...value.comprador } as unknown as DatosComprador);
            }
        });
        return () => subscription.unsubscribe();
    }

    // Efecto para manejar reservas según el step
    const onStepChange = () => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        const handleReserveOperations = async () => {
            const startTime = Date.now();
            setError(null);

            if (step === WIZARD_STEPS.DATOS_COMPRADOR) {
                setIsLoading(true);
            }

            try {
                if (step === WIZARD_STEPS.RESUMEN && reservaId) {
                    await release(signal);
                }

                if (step === WIZARD_STEPS.DATOS_COMPRADOR && !reservaId) {
                    if (!funcion?.id) {
                        throw new Error('No se ha seleccionado una función');
                    }
                    if (!entradas || entradas.length === 0) {
                        throw new Error('No hay entradas seleccionadas');
                    }
                    await reserve(entradas, signal);
                }

                setIsLoading(false);
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    setIsLoading(false);
                    return;
                }

                const errorMessage = error instanceof Error
                    ? error.message
                    : 'Error desconocido al procesar la reserva';

                // Mínimo 1 segundo de loading para UX
                const elapsedTime = Date.now() - startTime;
                if (elapsedTime < 1000) {
                    await new Promise(resolve => setTimeout(resolve, 1000 - elapsedTime));
                }

                setIsLoading(false);
                setError(errorMessage);
                setCompraStatus({ status: COMPRA_STATUS.RECHAZADA, message: errorMessage, compraId: null });
            }
        };

        handleReserveOperations();

        return () => {
            abortController.abort();
            setIsLoading(false);
        };
    }

    useEffect(syncCompradorStore, [methods, setComprador]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(onStepChange, [step]);

    return {
        // State
        isLoading,
        error,
        hideBack,

        // Handlers
        handleBack,
        handlePanelClose,
    };
};
